import type { AnyFilter, AnySerializedFilter } from "../filter"
import type { AnyFilterSchemaDefConfig } from "./typings"

export interface FilterSchemaConfig<TData> {
	filterDefs: AnyFilterSchemaDefConfig<TData>[]
}

export class FilterSchema<TData> {
	readonly filterDefs: Record<string, AnyFilterSchemaDefConfig<TData>>

	constructor(config: FilterSchemaConfig<TData>) {
		const { filterDefs } = config

		this.filterDefs = FilterSchema.initFilterDefs(filterDefs)
	}

	static create<TData>(config: FilterSchemaConfig<TData>) {
		return new FilterSchema(config)
	}

	private static initFilterDefs<TData>(filterDefConfigs: AnyFilterSchemaDefConfig<TData>[]) {
		const filterDefsMap: Record<string, AnyFilterSchemaDefConfig<TData>> = {}

		for (const filterDefConfig of filterDefConfigs) {
			filterDefsMap[filterDefConfig.filterDef.id] = filterDefConfig
		}

		return filterDefsMap
	}

	getAccessorFn = (filterDefId: string) => {
		const filterDef = this.filterDefs[filterDefId]

		if (!filterDef) {
			throw new Error(`FilterSchema.getAccessorFn: FilterDef "${filterDefId}" not found`)
		}

		return filterDef.accessorFn
	}

	getFilterDef = (filterDefId: string) => {
		const filterDef = this.filterDefs[filterDefId]

		if (!filterDef) {
			throw new Error(`FilterSchema.getFilterDef: FilterDef "${filterDefId}" not found`)
		}

		return filterDef.filterDef
	}

	serializeFilters = (filters: AnyFilter[]): AnySerializedFilter[] => {
		return filters.map((filter) => {
			const filterDef = filter.filterDef
			return filterDef.serialize(filter)
		})
	}

	deserializeFilters = (filters: AnySerializedFilter[]): AnyFilter[] => {
		const ret: AnyFilter[] = []

		for (const filter of filters) {
			if (!(filter.filterDefId in this.filterDefs)) {
				console.warn(`FilterSchema.deserializeFilters: FilterDef "${filter.filterDefId}" not found`)
				continue
			}

			const filterDef = this.getFilterDef(filter.filterDefId)
			ret.push(filterDef.deserialize(filter))
		}

		return ret
	}
}
